import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useSnackbar } from 'notistack';
import GranjaMovimientosLinkButton from '../../components/granja/GranjaMovimientosLinkButton';
import GranjaSucursalConfigDialog from '../../components/granja/GranjaSucursalConfigDialog';
import LoteSelectorButtons from '../../components/granja/LoteSelectorButtons';
import NumericKeypad from '../../components/granja/NumericKeypad';
import { useFechaOperativa } from '../../contexts/FechaOperativaContext';
import { useSucursalesUsuarioContext } from '../../contexts/SucursalesUsuarioContext';
import granjaService, { GranjaConfig, resolveNroSucOperativo } from '../../services/granjaService';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  formatMaplesYUnidades,
  HUEVOS_POR_MAPLE,
  maplesYHuevosAHuevos,
} from '../../utils/granjaCantidades';
import { toLocalDateStringYMD } from '../../utils/formatters';
import { readGlobalNroSuc } from '../../utils/nroSucPreferida';

type CampoCantidad = 'maples' | 'huevos';

const ValorCampo: React.FC<{
  label: string;
  value: string;
  active: boolean;
  onFocus: () => void;
}> = ({ label, value, active, onFocus }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onFocus}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onFocus();
      }
    }}
    sx={{
      flex: 1,
      bgcolor: active ? '#e3f2fd' : '#f0f4f8',
      borderRadius: 2,
      px: 2,
      py: 1.5,
      border: '2px solid',
      borderColor: active ? '#1976d2' : 'transparent',
      cursor: 'pointer',
      outline: 'none',
      '&:focus-visible': { borderColor: '#1976d2' },
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
      {label}
    </Typography>
    <Typography
      sx={{
        textAlign: 'right',
        fontSize: '2rem',
        fontWeight: 700,
        letterSpacing: 1,
        minHeight: 40,
        color: '#1565c0',
        lineHeight: 1.2,
      }}
    >
      {value || '0'}
    </Typography>
  </Box>
);

const GranjaRecoleccion: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { preferidaNro, preferidaSucursal, sucursales } = useSucursalesUsuarioContext();
  const { fechaYmd } = useFechaOperativa();
  const diaOperativo = fechaYmd ?? toLocalDateStringYMD();
  const [config, setConfig] = useState<GranjaConfig | null>(null);
  const [loteCodigo, setLoteCodigo] = useState<string | null>(null);
  const [campoActivo, setCampoActivo] = useState<CampoCantidad>('maples');
  const [maplesStr, setMaplesStr] = useState('');
  const [huevosStr, setHuevosStr] = useState('');
  const [saving, setSaving] = useState(false);
  const [acumulado, setAcumulado] = useState<Record<string, number>>({});
  const [configSucursalOpen, setConfigSucursalOpen] = useState(false);
  const [nroSucLocal, setNroSucLocal] = useState<number | null>(() => readGlobalNroSuc());

  useEffect(() => {
    (async () => {
      try {
        const cfg = await granjaService.getConfig();
        setConfig(cfg);
        const first = cfg.lotes.find((l) => l.activo);
        if (first) setLoteCodigo(first.codigo);
      } catch {
        enqueueSnackbar('Error al cargar configuración', { variant: 'error' });
      }
    })();
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (preferidaNro && preferidaNro > 0) {
      setNroSucLocal(preferidaNro);
    }
  }, [preferidaNro]);

  const lote = useMemo(
    () => config?.lotes.find((l) => l.codigo === loteCodigo) ?? null,
    [config, loteCodigo],
  );

  const nroSucEfectivo =
    (preferidaNro && preferidaNro > 0 ? preferidaNro : null) ??
    (nroSucLocal && nroSucLocal > 0 ? nroSucLocal : null) ??
    readGlobalNroSuc();

  const etiquetaSucursal = useMemo(() => {
    if (!nroSucEfectivo) return null;
    if (preferidaSucursal && Number(preferidaSucursal.nroSuc) === nroSucEfectivo) {
      return `${preferidaSucursal.nroSuc} - ${preferidaSucursal.nombreSucursal?.trim() || `Sucursal ${preferidaSucursal.nroSuc}`}`;
    }
    const found = sucursales.find((s) => Number(s.nroSuc) === nroSucEfectivo);
    if (found) {
      return `${found.nroSuc} - ${found.nombreSucursal?.trim() || `Sucursal ${found.nroSuc}`}`;
    }
    return `Sucursal ${nroSucEfectivo}`;
  }, [nroSucEfectivo, preferidaSucursal, sucursales]);

  const cantidadHuevos = maplesYHuevosAHuevos(
    Number(maplesStr) || 0,
    Number(huevosStr) || 0,
  );
  const cantidadComoMaples = cantidadHuevos / HUEVOS_POR_MAPLE;

  const valorActivo = campoActivo === 'maples' ? maplesStr : huevosStr;
  const setValorActivo = campoActivo === 'maples' ? setMaplesStr : setHuevosStr;

  const handleRegistrar = async () => {
    if (!config?.depositoGranja) {
      enqueueSnackbar('Configurá el depósito Granja', { variant: 'warning' });
      return;
    }
    if (!lote) {
      enqueueSnackbar('Seleccioná un lote', { variant: 'warning' });
      return;
    }
    if (cantidadHuevos <= 0) {
      enqueueSnackbar('Ingresá una cantidad', { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);
      const nroSuc = await resolveNroSucOperativo(preferidaNro ?? nroSucLocal);
      if (!nroSuc) {
        enqueueSnackbar('No hay sucursal operativa asignada al usuario', { variant: 'warning' });
        setConfigSucursalOpen(true);
        return;
      }
      const hora = new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
      await granjaService.registrarRecoleccion({
        codigoLote: lote.codigo,
        cantidad: cantidadHuevos,
        nroSuc,
        fecha: diaOperativo,
        observaciones: `Ronda ${hora} — ${maplesStr || 0} maples + ${huevosStr || 0} huevos (${cantidadHuevos} u.)`,
      });
      setAcumulado((prev) => ({
        ...prev,
        [lote.codigo]: (prev[lote.codigo] || 0) + cantidadHuevos,
      }));
      setMaplesStr('');
      setHuevosStr('');
      setCampoActivo('maples');
      enqueueSnackbar(
        `Registrados ${formatMaplesYUnidades(cantidadComoMaples)} (${cantidadHuevos} u.) en ${lote.codigo}`,
        { variant: 'success' },
      );
    } catch (err: unknown) {
      enqueueSnackbar(getApiErrorMessage(err, 'Error al registrar recolección'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  Recolección
                </Typography>
                <Tooltip title="Configurar sucursal operativa">
                  <IconButton
                    size="small"
                    color="primary"
                    aria-label="Configurar sucursal operativa"
                    onClick={() => setConfigSucursalOpen(true)}
                  >
                    <SettingsIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
              <GranjaMovimientosLinkButton tipo="RECOLECCION" />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Ingreso de maples en bruto por lote. No genera stock vendible hasta clasificar.
            </Typography>
            <Typography variant="body2" color={nroSucEfectivo ? 'text.secondary' : 'warning.main'}>
              Sucursal operativa:{' '}
              <strong>{etiquetaSucursal ?? 'sin configurar'}</strong>
            </Typography>

            <LoteSelectorButtons
              lotes={config?.lotes ?? []}
              selected={loteCodigo}
              onSelect={setLoteCodigo}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <ValorCampo
                label="Maples"
                value={maplesStr}
                active={campoActivo === 'maples'}
                onFocus={() => setCampoActivo('maples')}
              />
              <ValorCampo
                label="Huevos sueltos"
                value={huevosStr}
                active={campoActivo === 'huevos'}
                onFocus={() => setCampoActivo('huevos')}
              />
            </Stack>

            <NumericKeypad
              value={valorActivo}
              onChange={setValorActivo}
              maxDecimals={0}
              showValue={false}
            />

            <Typography variant="body1">
              Total ronda:{' '}
              <strong>
                {formatMaplesYUnidades(cantidadComoMaples)} ({cantidadHuevos} u.)
              </strong>
              {loteCodigo ? (
                <>
                  {' '}
                  · Acumulado {loteCodigo}:{' '}
                  <strong>
                    {formatMaplesYUnidades((acumulado[loteCodigo] || 0) / HUEVOS_POR_MAPLE)} (
                    {acumulado[loteCodigo] || 0} u.)
                  </strong>
                </>
              ) : null}
            </Typography>

            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={saving || cantidadHuevos <= 0}
              onClick={handleRegistrar}
              sx={{ minHeight: 56, fontSize: '1.1rem', fontWeight: 700 }}
            >
              {saving ? 'Guardando…' : 'Registrar ronda'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <GranjaSucursalConfigDialog
        open={configSucursalOpen}
        onClose={() => setConfigSucursalOpen(false)}
        onSaved={(nro) => setNroSucLocal(nro)}
      />
    </Box>
  );
};

export default GranjaRecoleccion;
